/**
 * Rezervasyon kaydetmeden önce "otel (satıcı) sistemde var mı" ve "oda tipi sistemde var mı"
 * kontrolü yapan, ikisini de yoksa otomatik ekleyen yardımcı fonksiyonlar.
 *
 * Akış:
 *   1) shoppingCartToRezervasyon ile ShoppingCart -> Rezervasyon dönüşümü yapılır (senkron, saf fonksiyon).
 *   2) Rezervasyondaki her kalem için otel adı SaticiAra endpoint'ine gönderilir.
 *      Bulunamazsa SaticiKayit ile otomatik eklenir.
 *   3) Her kalem için oda tipi adı (booking.RateName) OdaTipleriListe endpoint'ine gönderilir.
 *      Bulunamazsa OdaTipiKayit ile otomatik eklenir (BU ENDPOINT ADI VARSAYIM, bkz. aşağıdaki not).
 *   4) Tüm kalemlerin satici ve kalemOtel.odaTipi bilgisi güncellendikten sonra RezervasyonKaydet çağrılır.
 *
 * NOT (oda tipi kayıt endpoint'i hakkında): Paylaştığınız `Request { Ara, OdaTipi }` sınıfı için
 * endpoint adı belirtilmemişti; SaticiAra/SaticiKayit örüntüsüne kıyasla "OdaTipiKayit" olarak
 * varsaydım. Farklıysa aşağıdaki `odaTipiEkle` fonksiyonundaki endpoint string'ini güncelleyin.
 *
 * Kullanım (Vue component içinde):
 *   import { shoppingCartToRezervasyon } from './shoppingCartToRezervasyon';
 *   import { kaydetRezervasyon, SATICI_TIPI } from './rezervasyonuKaydet';
 *
 *   methods: {
 *     async rezervasyonuKaydetTiklandi() {
 *       // sirketNo verilmezse otomatik olarak localStorage'daki
 *       // credentials.CoperSirketNo kullanılır; farklı bir değer geçmek isterseniz
 *       // options.sirketNo ile override edebilirsiniz.
 *       const sonuc = await kaydetRezervasyon(this, this.shoppingCartResponse, {
 *         cariKod: this.cariKod
 *       });
 *       if (!sonuc.basarili) {
 *         // sonuc.hata -> hata mesajı
 *       }
 *     }
 *   }
 */

// shoppingCartToRezervasyon.js dosyasındaki dönüştürücüyü içeri alıyoruz.
// Projenizde CommonJS kullanılıyorsa require, ES module kullanılıyorsa import ile değiştirin.
import { shoppingCartToRezervasyon } from './shopcartToRezervasyon.js';
import tool from "../../../coper/tools.js";
// C# tarafındaki SaticiTipi enum'unun karşılığı. Şu an sadece Otel (1) kullanılıyor,
// ileride başka tiplerde de aynı akışı kullanmak isterseniz diye tamamı eklendi.
const SATICI_TIPI = {
  Otel: 1,
  Arac: 2,
  Restaurant: 3,
  Acente: 4,
  RentACar: 5,
  Rehber: 6,
  Muze: 7,
  Charter: 8,
  Gemi: 9,
  Diger: 10,
  Vize: 11,
  TurOperatoru: 12,
  Sigorta: 14,
  Feribot: 15,
  Golf: 16,
  PaketTur: 17
};

/**
 * `value ?? fallback` yerine kullanılan yardımcı: sadece null/undefined ise fallback döner,
 * 0/false/'' gibi geçerli falsy değerleri korur. (Projede ?? / ?. desteklenmiyor.)
 */
function orDefault(value, fallback) {
  return (value === null || value === undefined) ? fallback : value;
}

/**
 * Türkçe karakter/boşluk/case farklarını yok sayarak karşılaştırma yapabilmek için normalize eder.
 */
function normalizeText(str) {
  if (!str) return '';
  return str
    .toLocaleUpperCase('tr-TR')
    .replace(/İ/g, 'I')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * SaticiAra endpoint'ini çağırır.
 * @param {Object} vm - Vue component instance'ı (tool.http_post(this, ...) için)
 * @param {string} aciklama - Aranacak satıcı adı
 * @param {number} [tip=SATICI_TIPI.Otel] - SaticiTipi (ör. Otel=1, TurOperatoru=12)
 * @returns {Promise<Array>} Satıcı listesi
 */
function saticiAra(vm, aciklama, tip = SATICI_TIPI.Otel) {
  return new Promise((resolve, reject) => {
    tool.http_post(vm, 'SaticiAra', {
      id: '',
      kod: '',
      aciklama: aciklama,
      tip: tip,
      YurtIcDis: 0
    }).then(
      (data) => resolve(Array.isArray(data) ? data : []),
      (err) => reject(err)
    );
  });
}

/**
 * Verilen ada, verilen SaticiTipi için satıcı listesinde TAM eşleşen kaydı arar.
 * (isim ya da kod alanı normalize edildiğinde adla birebir aynıysa eşleşme sayılır.)
 * @param {Object} vm
 * @param {string} isim
 * @param {number} tip - SaticiTipi
 * @returns {Promise<Object|null>} Eşleşen satıcı kaydı (SaticiAra'nın tam şekli), yoksa null
 */
async function saticiBul(vm, isim, tip) {
  const liste = await saticiAra(vm, isim, tip);
  const hedef = normalizeText(isim);

  const eslesenler = liste.filter(
    (s) => normalizeText(s.isim) === hedef || normalizeText(s.kod) === hedef
  );

  if (eslesenler.length === 0) return null;

  if (eslesenler.length > 1) {
    console.warn(`"${isim}" için birden fazla satıcı eşleşmesi bulundu, ilki kullanılacak:`, eslesenler);
  }

  return eslesenler[0];
}

/**
 * Otel adına özel saticiBul sarmalayıcısı (geriye dönük uyumluluk için).
 * @param {Object} vm
 * @param {string} hotelName
 * @returns {Promise<Object|null>}
 */
function oteliBul(vm, hotelName) {
  return saticiBul(vm, hotelName, SATICI_TIPI.Otel);
}

/**
 * window.localStorage'daki "credentials" içinden CoperSirketNo'yu okur.
 * SaticiKayit çağrısı için SirketNo bilgisi kaynağı budur.
 * @returns {number|null}
 */
function coperSirketNoOku() {
  try {
    const credentials = JSON.parse(window.localStorage.getItem('credentials'));
    return orDefault(credentials && credentials.CoperSirketNo, null);
  } catch (err) {
    console.warn('credentials localStorage\'dan okunamadı:', err);
    return null;
  }
}

/**
 * SaticiKayit endpoint'ine yeni bir Satici kaydı açar (herhangi bir SaticiTipi için).
 * @param {Object} vm
 * @param {Object} params
 * @param {string} params.kod - Satici.Kod (zorunlu, 1-50 karakter)
 * @param {string} params.isim - Satici.Isim (zorunlu, 1-100 karakter)
 * @param {number} params.tip - Satici.SaticiTipi
 * @param {number} [params.sirketNo] - Satici.SirketNo. Verilmezse localStorage'daki
 *   credentials.CoperSirketNo kullanılır.
 * @returns {Promise<{ id: string|number, kod: string, isim: string, tip: number }>}
 */
function saticiEkle(vm, { kod, isim, tip, sirketNo }) {
  // Kod boşsa isim'den türetip 50 karakterle sınırlıyoruz, çünkü Kod alanı zorunlu.
  const guvenliKod = (kod && String(kod).trim()) || isim;
  const sirketNoDegeri = orDefault(sirketNo, coperSirketNoOku());

  return new Promise((resolve, reject) => {
    tool.http_post(vm, 'SaticiKayit', {
      SaticiTipi: tip,
      Ref: 0, // yeni kayıt
      SirketNo: sirketNoDegeri,
      Kod: String(guvenliKod).slice(0, 50),
      Isim: String(isim).slice(0, 100)
    }).then(
      (data) => {
        // Response, body.Satici içinde SaticiKayit'e gönderilenle aynı yapıda (PascalCase) dönüyor.
        const satici = orDefault(data && data.Satici, data);
        resolve({
          id: orDefault(satici && satici.Ref, null),
          kod: orDefault(satici && satici.Kod, guvenliKod),
          isim: orDefault(satici && satici.Isim, isim),
          tip: orDefault(satici && satici.SaticiTipi, tip)
        });
      },
      (err) => reject(err)
    );
  });
}

/**
 * Otel için saticiEkle sarmalayıcısı (geriye dönük uyumluluk için).
 * @param {Object} vm
 * @param {Object} params
 * @param {string} params.kod
 * @param {string} params.isim
 * @param {number} [params.sirketNo]
 * @returns {Promise<{ id: string|number, kod: string, isim: string }>}
 */
function oteliEkle(vm, { kod, isim, sirketNo }) {
  return saticiEkle(vm, { kod, isim, tip: SATICI_TIPI.Otel, sirketNo });
}

/**
 * turOperatoru gibi tam Satici şekli (SaticiAra'nın döndürdüğü tüm alanlar) bekleyen yerler için,
 * eksik bilgileri null/varsayılanla tamamlayarak tam şekli üretir. SaticiAra sonucu zaten tam
 * şekilde geldiğinden ona dokunmadan geçirir; SaticiKayit sonucu (id/kod/isim/tip) için eksik
 * alanları doldurur.
 * @param {Object} partial
 */
function tamSaticiSekli(partial) {
  partial = partial || {};
  return {
    YurtIci_YurtDisi: orDefault(partial.YurtIci_YurtDisi, 0),
    CariKod: orDefault(partial.CariKod, null),
    KDV: orDefault(partial.KDV, 0.0),
    Yer: orDefault(partial.Yer, null),
    Ulke: orDefault(partial.Ulke, null),
    Sehir: orDefault(partial.Sehir, null),
    SaticiKonumu: orDefault(partial.SaticiKonumu, null),
    Web: orDefault(partial.Web, null),
    Tel1: orDefault(partial.Tel1, null),
    Tel2: orDefault(partial.Tel2, null),
    Fax: orDefault(partial.Fax, null),
    Teleks: orDefault(partial.Teleks, null),
    EMail: orDefault(partial.EMail, null),
    GSM: orDefault(partial.GSM, null),
    Adres: orDefault(partial.Adres, null),
    id: orDefault(partial.id, ''),
    kod: orDefault(partial.kod, ''),
    isim: orDefault(partial.isim, ''),
    tip: orDefault(partial.tip, 0),
    intField: orDefault(partial.intField, null),
    tumSirketler: orDefault(partial.tumSirketler, false),
    sayfaAdi: orDefault(partial.sayfaAdi, null)
  };
}

/**
 * Tur operatörü için saticiBul/saticiEkle sarmalayıcısı. Bulunamazsa otomatik olarak
 * SaticiKayit ile ekler ve her durumda tam Satici şeklinde (kalem.turOperatoru'ya
 * doğrudan konabilecek) bir sonuç döner.
 * @param {Object} vm
 * @param {string} providerAdi - booking.Provider (ör. "Paximum")
 * @param {Object} [options]
 * @param {number} [options.sirketNo]
 * @returns {Promise<Object>} Tam Satici şekli
 */
async function turOperatoruBulYaDaEkle(vm, providerAdi, options = {}) {
  const bulunan = await saticiBul(vm, providerAdi, SATICI_TIPI.TurOperatoru);
  if (bulunan) {
    return tamSaticiSekli(bulunan);
  }

  const eklenen = await saticiEkle(vm, {
    kod: providerAdi,
    isim: providerAdi,
    tip: SATICI_TIPI.TurOperatoru,
    sirketNo: options.sirketNo
  });

  return tamSaticiSekli(eklenen);
}

/**
 * OdaTipleriListe endpoint'ini çağırır.
 * @param {Object} vm
 * @param {string} aciklama - Aranacak oda tipi adı (booking.RateName)
 * @returns {Promise<Array>} OtelOdaTipi listesi
 */
function odaTipiAra(vm, aciklama) {
  return new Promise((resolve, reject) => {
    tool.http_post(vm, 'OdaTipleriList', { Ara: aciklama }).then(
      (res) => {
        // Response.body.data şeklinde geliyor; farklı bir sarmalama ihtimaline karşı
        // res.data'ya da fallback yapıyoruz.
        const liste = orDefault(res && res.body && res.body.data, orDefault(res && res.data, []));
        resolve(Array.isArray(liste) ? liste : []);
      },
      (err) => reject(err)
    );
  });
}

/**
 * Verilen oda tipi adına, OdaTipleriListe sonucunda TAM eşleşen kaydı arar.
 * AltOdaTipleri (varsa) da düzleştirilip aramaya dahil edilir.
 * @param {Object} vm
 * @param {string} odaTipiAdi
 * @returns {Promise<Object|null>} { Kod, Aciklama, Yetiskin, Cocuk1, Cocuk2 } veya null
 */
async function odaTipiniBul(vm, odaTipiAdi) {
  const liste = await odaTipiAra(vm, odaTipiAdi);
  const hedef = normalizeText(odaTipiAdi);

  const tumTipler = liste.flatMap((t) => [t, ...(t.AltOdaTipleri || [])]);

  const eslesenler = tumTipler.filter(
    (t) => normalizeText(t.Aciklama) === hedef || normalizeText(t.Kod) === hedef
  );

  if (eslesenler.length === 0) return null;

  if (eslesenler.length > 1) {
    console.warn(`"${odaTipiAdi}" için birden fazla oda tipi eşleşmesi bulundu, ilki kullanılacak:`, eslesenler);
  }

  return eslesenler[0];
}

/**
 * Oda tipi sistemde yoksa yeni kayıt açar.
 *
 * NOT: Bu endpoint'in adı paylaşılmadı; SaticiAra/SaticiKayit örüntüsüne kıyasla
 * "OdaTipiKayit" olarak varsayıldı. Gerçek adı farklıysa değiştirin.
 *
 * @param {Object} vm
 * @param {Object} params
 * @param {string} params.kod - OdaTipi.Kod
 * @param {string} params.aciklama - OdaTipi.Aciklama (booking.RateName)
 * @param {number} [params.yetiskin=0] - OdaTipi.Yetiskin
 * @param {number} [params.cocuk1=0] - OdaTipi.Cocuk1 (çocuk, 2-11 yaş)
 * @param {number} [params.cocuk2=0] - OdaTipi.Cocuk2 (bebek, 2 yaş altı)
 * @returns {Promise<{ kod: string, aciklama: string, yetiskin: number, cocuk1: number, cocuk2: number }>}
 */
function odaTipiEkle(vm, { kod, aciklama, yetiskin = 0, cocuk1 = 0, cocuk2 = 0 }) {
  const guvenliKod = (kod && String(kod).trim()) || aciklama;

  return new Promise((resolve, reject) => {
    tool.http_post(vm, 'OdaTipiSave', {
      Ara: aciklama,
      OdaTipi: {
        Kod: guvenliKod,
        Aciklama: aciklama,
        Yetiskin: yetiskin,
        Cocuk1: cocuk1,
        Cocuk2: cocuk2
      }
    }).then(
      (res) => {
        // Tam dönüş formatı bilinmediğinden birkaç olası şekli deniyoruz.
        const otd = orDefault(
          res && res.body && res.body.data,
          orDefault(
            res && res.body && res.body.OdaTipi,
            orDefault(
              res && res.data,
              orDefault(res && res.OdaTipi, res)
            )
          )
        );
        resolve({
          kod: orDefault(otd && otd.Kod, guvenliKod),
          aciklama: orDefault(otd && otd.Aciklama, aciklama),
          yetiskin: orDefault(otd && otd.Yetiskin, yetiskin),
          cocuk1: orDefault(otd && otd.Cocuk1, cocuk1),
          cocuk2: orDefault(otd && otd.Cocuk2, cocuk2)
        });
      },
      (err) => reject(err)
    );
  });
}

/**
 * ShoppingCart'ı Rezervasyon'a çevirir, içindeki otelleri SaticiAra ile doğrular,
 * bulunamayanları SaticiKayit ile ekler ve RezervasyonKaydet'i çağırır.
 *
 * @param {Object} vm - Vue component instance'ı
 * @param {Object} shoppingCartResponse - { ShoppingCart: {...} }
 * @param {Object} [options] - shoppingCartToRezervasyon'a geçilecek seçenekler (cariKod)
 *   + sirketNo (SaticiKayit için; verilmezse localStorage'daki credentials.CoperSirketNo kullanılır)
 * @returns {Promise<{ basarili: boolean, hata?: string, rezervasyon?: Object }>}
 */
async function kaydetRezervasyon(vm, shoppingCartResponse, options = {}) {
  const { Rezervasyon: rezervasyon, odaTipiIhtiyaclari } = shoppingCartToRezervasyon(shoppingCartResponse, options);

  // otelAdi -> o otele ait ilk kalemin satici bilgisi (yeni kayıt açılırken varsayılan Kod için)
  const otelBilgisi = {};
  rezervasyon.kalemler.forEach((k) => {
    if (!otelBilgisi[k.satici.isim]) {
      otelBilgisi[k.satici.isim] = k.satici; // { id, kod, isim, tip }
    }
  });

  const bulunanOteller = {};
  const bulunanOdaTipleri = {}; // odaTipiAdi -> { kod, aciklama, ... }
  const bulunanTurOperatorleri = {}; // providerAdi -> tam Satici şekli

  try {
    for (const otelAdi of Object.keys(otelBilgisi)) {
      let satici = await oteliBul(vm, otelAdi);

      if (!satici) {
        // Otel sistemde yok -> otomatik olarak SaticiKayit ile ekleniyor
        satici = await oteliEkle(vm, {
          kod: otelBilgisi[otelAdi].kod,
          isim: otelAdi,
          sirketNo: options.sirketNo
        });
      }

      bulunanOteller[otelAdi] = satici;
    }

    for (let i = 0; i < rezervasyon.kalemler.length; i++) {
      const kalem = rezervasyon.kalemler[i];
      const odaTipiAdi = kalem.kalemOtel.odaTipi.isim;
      if (!odaTipiAdi) continue; // oda tipi adı yoksa kontrol etmeden geçiyoruz

      if (!bulunanOdaTipleri[odaTipiAdi]) {
        let odaTipi = await odaTipiniBul(vm, odaTipiAdi);

        if (!odaTipi) {
          // Oda tipi sistemde yok -> otomatik olarak OdaTipiKayit ile ekleniyor
          const ihtiyac = odaTipiIhtiyaclari[i] || {};
          odaTipi = await odaTipiEkle(vm, {
            kod: odaTipiAdi,
            aciklama: odaTipiAdi,
            yetiskin: ihtiyac.yetiskin,
            cocuk1: ihtiyac.cocuk1,
            cocuk2: ihtiyac.cocuk2
          });
        } else {
          // odaTipiniBul, backend alan adlarıyla (Kod/Aciklama) dönüyor; normalize ediyoruz.
          odaTipi = { kod: odaTipi.Kod, aciklama: odaTipi.Aciklama };
        }

        bulunanOdaTipleri[odaTipiAdi] = odaTipi;
      }
    }

    for (const kalem of rezervasyon.kalemler) {
      const providerAdi = kalem.turOperatoru.isim;
      if (!providerAdi) continue; // provider adı yoksa kontrol etmeden geçiyoruz

      if (!bulunanTurOperatorleri[providerAdi]) {
        bulunanTurOperatorleri[providerAdi] = await turOperatoruBulYaDaEkle(vm, providerAdi, {
          sirketNo: options.sirketNo
        });
      }
    }
  } catch (err) {
    return { basarili: false, hata: `Otel/oda tipi/tur operatörü kontrol-ekleme sırasında hata: ${err}` };
  }

  // Her kalemdeki satici, kalemOtel.odaTipi ve turOperatoru bilgisini bulunan/eklenen
  // gerçek değerlerle güncelliyoruz.
  rezervasyon.kalemler.forEach((kalem) => {
    const satici = bulunanOteller[kalem.satici.isim];
    kalem.satici.id = satici.id;
    kalem.satici.kod = satici.kod;

    const odaTipiAdi = kalem.kalemOtel.odaTipi.isim;
    if (odaTipiAdi && bulunanOdaTipleri[odaTipiAdi]) {
      kalem.kalemOtel.odaTipi.kod = bulunanOdaTipleri[odaTipiAdi].kod;
      kalem.kalemOtel.odaTipi.isim = bulunanOdaTipleri[odaTipiAdi].aciklama;
    }

    const providerAdi = kalem.turOperatoru.isim;
    if (providerAdi && bulunanTurOperatorleri[providerAdi]) {
      // turOperatoru alanının TAMAMI, bulunan/eklenen satıcı kaydıyla değiştiriliyor.
      kalem.turOperatoru = bulunanTurOperatorleri[providerAdi];
    }
  });

  try {
    await new Promise((resolve, reject) => {
      tool.http_post(vm, 'RezervasyonKaydet', { Rezervasyon: rezervasyon }).then(
        () => resolve(),
        (err) => reject(err)
      );
    });
  } catch (err) {
    return { basarili: false, hata: `Rezervasyon kaydedilirken hata: ${err}`, rezervasyon };
  }

  return { basarili: true, rezervasyon };
}

export {
  kaydetRezervasyon,
  oteliBul,
  oteliEkle,
  saticiAra,
  saticiBul,
  saticiEkle,
  tamSaticiSekli,
  turOperatoruBulYaDaEkle,
  odaTipiAra,
  odaTipiniBul,
  odaTipiEkle,
  normalizeText,
  SATICI_TIPI
};